import React, { useState, useEffect, useMemo } from 'react';

const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
             (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const navigationItems = useMemo(() => [
    { id: 'about', label: 'Biography', icon: '👨‍💼' },
    { id: 'publications', label: 'Publications', icon: '📚' },
    { id: 'resume', label: 'Resume', icon: '📄' },
    { id: 'skills-projects', label: 'Projects', icon: '🚀' },
    { id: 'certificate', label: 'Certificates', icon: '🏆' },
    { id: 'contact', label: 'Contact', icon: '📧' }
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);

      // Update active section based on scroll position
      const sections = navigationItems.map(item => item.id);
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navigationItems]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Keyboard handler for mobile menu
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Premium Academic Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-700 ease-out ${
          isScrolled
            ? 'backdrop-blur-3xl shadow-2xl border-b'
            : 'bg-transparent'
        }`}
        style={{
          backgroundColor: isScrolled ? 'rgba(var(--bg-main-rgb), 0.95)' : 'transparent',
          borderColor: isScrolled ? 'var(--border)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(30px)' : 'none',
          WebkitBackdropFilter: isScrolled ? 'blur(30px)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Sophisticated Logo & Brand */}
            <button
              className="flex items-center space-x-3 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-2xl p-1"
              onClick={() => scrollToSection('about')}
              aria-label="Scroll to top"
            >
              {/* Blue-Themed Premium Logo */}
              <div className={`relative w-10 h-10 lg:w-12 lg:h-12 transition-all duration-700 group-hover:scale-110 group-hover:rotate-12 ${
                isScrolled
                  ? 'bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 shadow-2xl shadow-blue-500/40'
                  : `bg-gradient-to-br from-blue-500 via-indigo-600 to-blue-700 shadow-2xl shadow-blue-400/50 border-2 ${isDarkMode ? 'border-gray-600/50' : 'border-white/50'}`
              } ${
                isDarkMode
                  ? 'shadow-blue-400/60'
                  : 'shadow-blue-500/40'
              } rounded-2xl overflow-hidden backdrop-blur-2xl flex items-center justify-center`}>

                {/* Blue-Themed Neural Pattern */}
                <div className="absolute inset-0 opacity-20">
                  <svg viewBox="0 0 64 64" className="w-full h-full">
                    <defs>
                      <radialGradient id="blueNeuralGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9"/>
                        <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.6"/>
                        <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.3"/>
                      </radialGradient>
                      <pattern id="blueAiGrid" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                        <circle cx="4" cy="4" r="1" fill="url(#blueNeuralGlow)"/>
                        <line x1="4" y1="0" x2="4" y2="8" stroke="#60a5fa" strokeWidth="0.3" opacity="0.7"/>
                        <line x1="0" y1="4" x2="8" y2="4" stroke="#60a5fa" strokeWidth="0.3" opacity="0.7"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#blueAiGrid)"/>

                    {/* Dynamic Blue AI Connections */}
                    <g opacity="0.6">
                      <circle cx="16" cy="16" r="2.5" fill="#60a5fa">
                        <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite"/>
                        <animate attributeName="r" values="2;3;2" dur="3s" repeatCount="indefinite"/>
                      </circle>
                      <circle cx="48" cy="16" r="2" fill="#3b82f6">
                        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.5s" repeatCount="indefinite" begin="0.5s"/>
                        <animate attributeName="r" values="1.5;2.5;1.5" dur="2.5s" repeatCount="indefinite" begin="0.5s"/>
                      </circle>
                      <circle cx="32" cy="48" r="2.2" fill="#1e40af">
                        <animate attributeName="opacity" values="0.5;1;0.5" dur="2.8s" repeatCount="indefinite" begin="1s"/>
                        <animate attributeName="r" values="1.8;2.8;1.8" dur="2.8s" repeatCount="indefinite" begin="1s"/>
                      </circle>
                      <line x1="16" y1="16" x2="48" y2="16" stroke="#60a5fa" strokeWidth="0.8" opacity="0.5">
                        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="4s" repeatCount="indefinite"/>
                      </line>
                      <line x1="48" y1="16" x2="32" y2="48" stroke="#3b82f6" strokeWidth="0.8" opacity="0.5">
                        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="4s" repeatCount="indefinite" begin="1s"/>
                      </line>
                      <line x1="32" y1="48" x2="16" y2="16" stroke="#1e40af" strokeWidth="0.8" opacity="0.5">
                        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="4s" repeatCount="indefinite" begin="2s"/>
                      </line>
                    </g>
                  </svg>
                </div>

                {/* Modern Blue "S" Design */}
                <div className="relative z-20">
                  <svg viewBox="0 0 32 40" className="w-9 h-11">
                    <defs>
                      <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="white" stopOpacity="1"/>
                        <stop offset="50%" stopColor="#f0f9ff" stopOpacity="0.95"/>
                        <stop offset="100%" stopColor="#dbeafe" stopOpacity="0.9"/>
                      </linearGradient>
                      <filter id="blueShadow">
                        <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#1e40af" floodOpacity="0.3"/>
                      </filter>
                    </defs>

                    {/* Blue-Themed Premium "S" Letterform */}
                    <g filter="url(#blueShadow)">
                      <path
                        d="M8 10 Q12 6 18 8 Q24 10 22 16 Q20 18 16 18 Q12 18 10 20 Q8 22 10 24 Q12 26 16 26 Q20 26 22 24 Q24 22 22 28 Q20 34 14 32 Q8 30 10 24"
                        fill="url(#blueGradient)"
                        stroke="white"
                        strokeWidth="0.8"
                      />

                      {/* Blue inner highlight */}
                      <path
                        d="M10 12 Q13 9 17 10 Q20 11 19 15 Q18 17 15 17 Q12 17 11 19"
                        fill="none"
                        stroke="#f0f9ff"
                        strokeWidth="1.2"
                        opacity="0.8"
                      />

                      {/* Blue accent dot */}
                      <circle cx="18" cy="12" r="1" fill="#dbeafe" opacity="0.9"/>
                    </g>
                  </svg>
                </div>

                {/* Blue-themed Floating AI Particles */}
                <div className="absolute inset-0">
                  <div className="absolute top-3 right-3 w-2 h-2 bg-blue-100/90 rounded-full">
                    <div className="w-full h-full rounded-full animate-ping bg-blue-200/60"></div>
                  </div>
                  <div className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-blue-50/90 rounded-full">
                    <div className="w-full h-full rounded-full animate-pulse bg-blue-100/50"></div>
                  </div>
                  <div className="absolute top-1/2 left-2 w-1 h-1 bg-white/80 rounded-full animate-pulse delay-500"></div>
                  <div className="absolute bottom-1/2 right-2 w-1 h-1 bg-blue-50/70 rounded-full animate-pulse delay-1000"></div>
                </div>

                {/* Blue Hover Effects */}
                <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-blue-400/60 via-blue-500/40 to-indigo-600/60 blur-xl scale-110'
                    : 'bg-gradient-to-br from-blue-300/70 via-blue-400/50 to-blue-500/70 blur-xl scale-110'
                }`}></div>

                {/* Blue-themed Border Ring */}
                <div className="absolute inset-0 rounded-2xl border-2 border-blue-200/40 group-hover:border-blue-100/70 transition-all duration-700 group-hover:scale-105">
                  <div className="absolute inset-0 rounded-2xl border border-blue-100/20 group-hover:border-blue-50/40 transition-all duration-700 group-hover:scale-110"></div>
                </div>

                {/* Blue Corner Accents */}
                <div className="absolute top-1 left-1 w-3 h-3 border-l-2 border-t-2 border-blue-100/50 rounded-tl-lg group-hover:border-blue-50/80 transition-all duration-500"></div>
                <div className="absolute bottom-1 right-1 w-3 h-3 border-r-2 border-b-2 border-blue-100/50 rounded-br-lg group-hover:border-blue-50/80 transition-all duration-500"></div>
              </div>

              {/* Enhanced Brand Information */}
              <div className="hidden md:block">
                <h1 className="text-2xl lg:text-3xl font-bold tracking-tight transition-all duration-500 group-hover:text-primary" style={{ color: 'var(--text-primary)' }}>
                  Sayem
                </h1>
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center space-x-2">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`group relative px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-500 hover:scale-105 ${
                    activeSection === item.id
                      ? 'text-white shadow-xl'
                      : 'hover:bg-bg-surface hover:text-primary'
                  }`}
                  style={{
                    backgroundColor: activeSection === item.id ? 'var(--primary)' : 'transparent',
                    color: activeSection === item.id ? '#ffffff' : 'var(--text-secondary)',
                    boxShadow: activeSection === item.id ? '0 10px 25px -5px rgba(76, 95, 215, 0.3)' : 'none'
                  }}
                >
                  <span className="flex items-center space-x-2">
                    <span className="text-base">{item.icon}</span>
                    <span>{item.label}</span>
                  </span>

                  {/* Active indicator */}
                  {activeSection === item.id && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#ffffff' }}></div>
                  )}

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-all duration-500 blur-sm"></div>
                </button>
              ))}
            </div>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                className={`group relative w-12 h-12 rounded-2xl transition-all duration-500 hover:scale-110 shadow-lg hover:shadow-xl border-2 border-transparent ${
                  isDarkMode ? 'bg-slate-800' : 'bg-bg-surface'
                } text-text-primary`}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  {isDarkMode ? (
                    <svg className="w-6 h-6 transition-transform duration-500 group-hover:rotate-180" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 transition-transform duration-500 group-hover:-rotate-45" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                  )}
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-all duration-500 blur-sm"></div>
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={toggleMobileMenu}
                className={`xl:hidden group relative w-12 h-12 rounded-2xl transition-all duration-500 hover:scale-110 shadow-lg border-2 border-transparent ${
                  isMobileMenuOpen ? 'text-white bg-red-500' : 'text-text-primary bg-bg-surface'
                }`}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  {isMobileMenuOpen ? (
                    // Close X icon when menu is open
                    <svg className="w-6 h-6 transition-transform duration-300 group-hover:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    // Hamburger icon when menu is closed
                    <div className="relative w-6 h-6">
                      <span className="absolute block h-0.5 w-6 transform transition-all duration-300 -translate-y-2 bg-current rounded-full"></span>
                      <span className="absolute block h-0.5 w-6 transform transition-all duration-300 bg-current rounded-full"></span>
                      <span className="absolute block h-0.5 w-6 transform transition-all duration-300 translate-y-2 bg-current rounded-full"></span>
                    </div>
                  )}
                </div>
                <div className={`absolute inset-0 rounded-2xl transition-all duration-500 blur-sm ${
                  isMobileMenuOpen
                    ? 'bg-gradient-to-r from-red-400/20 to-red-500/20 opacity-100'
                    : 'bg-gradient-to-r from-blue-400/20 to-blue-500/20 opacity-0 group-hover:opacity-100'
                }`}></div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Premium Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[9998] xl:hidden"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer animate-in fade-in duration-300"
            onClick={toggleMobileMenu}
            aria-label="Close menu"
          ></div>

          {/* Mobile Menu Panel */}
          <div
            className="absolute top-0 right-0 w-80 h-full transition-all duration-500 animate-in slide-in-from-right backdrop-blur-3xl shadow-2xl border-l"
            style={{
              backgroundColor: 'rgba(var(--bg-main-rgb), 0.98)',
              borderColor: 'var(--border)'
            }}
            onClick={(e) => e.stopPropagation()}
          >

            {/* Mobile Header */}
            <div className="p-6 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Navigation</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Explore my work</p>
                </div>
              </div>
            </div>

            {/* Mobile Navigation Links */}
            <div className="p-6 space-y-2">
              {navigationItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full group relative flex items-center space-x-4 p-4 rounded-2xl font-semibold text-left transition-all duration-500 hover:scale-105 ${
                    activeSection === item.id
                      ? 'text-white shadow-xl'
                      : 'hover:bg-bg-surface hover:text-primary'
                  }`}
                  style={{
                    backgroundColor: activeSection === item.id ? 'var(--primary)' : 'transparent',
                    color: activeSection === item.id ? '#ffffff' : 'var(--text-secondary)',
                    boxShadow: activeSection === item.id ? '0 10px 25px -5px rgba(76, 95, 215, 0.3)' : 'none',
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  <svg className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                </button>
              ))}
            </div>

            {/* Mobile Footer */}
            <div className="absolute bottom-6 left-6 right-6">
              <div className="p-4 rounded-2xl border" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)' }}>
                <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                  © 2025 Sayem
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
